import { useMemo } from "react";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";
import { adaptRecentLab, recentLabSortDate, type RecentLabItem } from "@/features/patient/diagnostics/utils/adaptRecentLab";

interface DiagnosticReportsResult {
  recentLabs: RecentLabItem[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

// Derived from ParsedRecordsContext instead of its own independent fetch —
// DiagnosticReport is a strict subset of that same unfiltered data, so a
// second API call here was pure duplication. No Provider needed: this is a
// pure selector over the one real fetch/loading/error/refresh state that
// ParsedRecordsContext owns. (The full Diagnostics & Imaging sidebar page
// still uses its own mock data — separate, bigger lift not done here.)
export function useDiagnosticReports(): DiagnosticReportsResult {
  const { records, isLoading, error, refresh } = useParsedRecords();
  const recentLabs = useMemo(() => {
    const reports = records.filter((r) => r.resourceType === "DiagnosticReport");
    const sorted = [...reports].sort((a, b) => recentLabSortDate(b).localeCompare(recentLabSortDate(a)));
    return sorted.map(adaptRecentLab);
  }, [records]);
  return { recentLabs, isLoading, error, refresh };
}
